import React, { useState } from 'react';
import { useClinicalDemo } from '../../contexts/ClinicalDemoContext';
import { categoryInfo } from '../../data/clinicalDemo';
import type { EntryCategory, EntryDraft } from '../../types/clinicalDemo';
import { errorClass, fieldClass, labelClass, primaryButton } from '../../utils/demoUi';
import { hasBeenReviewed } from '../../utils/clinicalDemo';
const categories = Object.keys(categoryInfo) as EntryCategory[];
export function EntryForm({
  visitId
}: {
  visitId: string;
}) {
  const {
    state,
    dispatch
  } = useClinicalDemo();
  const draft: EntryDraft = state.ui.entryDrafts[visitId] ?? {
    category: '',
    text: '',
    addendumTo: ''
  };
  const addendum = hasBeenReviewed(state, visitId);
  const visitEntries = state.entries.filter(entry => entry.visitId === visitId);
  const [error, setError] = useState('');
  const update = (patch: Partial<EntryDraft>) => dispatch({
    type: 'setEntryDraft',
    visitId,
    draft: {
      ...draft,
      ...patch
    }
  });
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.category) return setError('Choose the category for this entry.');
    if (!draft.text.trim()) return setError('Write the entry text.');
    setError('');
    dispatch({
      type: 'addEntry',
      visitId
    });
  };
  return <form onSubmit={submit} noValidate className="mt-5 rounded-xl border border-dashed border-line bg-white/70 p-4" data-id="element-173" data-mp="1ywzjv21dt1af">
      <p className="font-semibold text-ink-dark" data-id="element-174" data-mp="1u2li8hw4lxxb">{addendum ? 'Add an addendum' : 'Add fictional documentation'}</p>
      <p className="mt-0.5 text-xs text-ink-muted" data-id="element-175" data-mp="23uhtkun83kjh">
        {addendum ? 'This visit has been reviewed. Its entries are preserved; corrections and new context are appended.' : 'Categories are chosen by the clinician. Nothing is inferred automatically.'}
      </p>
      <label htmlFor={`${visitId}-category`} className={`mt-3 ${labelClass}`} data-id="element-176" data-mp="2dqkr1n74qv03">
        Category
      </label>
      <select id={`${visitId}-category`} value={draft.category} onChange={event => update({
      category: event.target.value as EntryCategory
    })} className={fieldClass} data-id="element-177" data-mp="ni2n15yyh7si">
        <option value="" data-id="element-178" data-mp="2ikz5vwnzlh32">Choose…</option>
        {categories.map(category => <option key={category} value={category} data-id="element-179" data-mp="2njabtwb5v7g0">
            {categoryInfo[category].label} — {categoryInfo[category].description}
          </option>)}
      </select>
      {addendum && <>
          <label htmlFor={`${visitId}-target`} className={`mt-3 ${labelClass}`} data-id="element-180" data-mp="2xi9m1jbkrb54">
            Relates to entry <span className="font-normal text-ink-muted" data-id="element-181" data-mp="31hqtgiakaicx">(optional)</span>
          </label>
          <select id={`${visitId}-target`} value={draft.addendumTo} onChange={event => update({
        addendumTo: event.target.value
      })} className={fieldClass} data-id="element-182" data-mp="36g22iqt18ou3">
            <option value="" data-id="element-183" data-mp="3g6wqq0pu9yi6">The visit as a whole</option>
            {visitEntries.map(entry => <option key={entry.id} value={entry.id} data-id="element-184" data-mp="3l6swa4e6pxzu">
                {entry.id} — {categoryInfo[entry.category].label}
              </option>)}
          </select>
        </>}
      <label htmlFor={`${visitId}-text`} className={`mt-3 ${labelClass}`} data-id="element-185" data-mp="1dk9bjye4eav8">
        Entry text
      </label>
      <textarea id={`${visitId}-text`} rows={3} value={draft.text} onChange={event => update({
      text: event.target.value
    })} className={fieldClass} data-id="element-186" data-mp="19iebex7xe3j1" />
      {error && <p className={errorClass} data-id="element-187" data-mp="14lxbl5rd60q4">{error}</p>}
      <button type="submit" className={`mt-3 ${primaryButton}`} data-id="element-188" data-mp="zljcwebnk34r">
        {addendum ? 'Append addendum' : 'Add entry'}
      </button>
    </form>;
}